import { createReport, getReport, updateReport, type Outcome } from './api';
import { addNoteDocument, patchAllocation, removeNoteDocument } from './api-parity';
import type { PendingFile } from './note-files';
import { toAllocationPatch, type Allocation } from './project-shares';
import type { Report, ReportInputWave2 } from './types';

/** What a save could not do once the note itself was saved; the note stays saved (#915 W1). */
export type MissedStep =
  | { readonly kind: 'allocation'; readonly code: string }
  | { readonly kind: 'upload'; readonly key: string; readonly name: string; readonly code: string }
  | { readonly kind: 'remove'; readonly documentId: string; readonly code: string };

export type SaveOutcome =
  | { readonly ok: false; readonly status: number; readonly code: string }
  | {
      readonly ok: true;
      readonly report: Report;
      readonly missed: readonly MissedStep[];
    };

export type NoteSaveApi = {
  readonly create: (input: ReportInputWave2) => Promise<Outcome<Report>>;
  readonly update: (
    id: string,
    version: string,
    body: ReportInputWave2,
  ) => Promise<Outcome<Report>>;
  readonly allocate: typeof patchAllocation;
  readonly upload: typeof addNoteDocument;
  readonly remove: typeof removeNoteDocument;
  readonly reload: (id: string) => Promise<Outcome<Report>>;
};

const API: NoteSaveApi = {
  create: createReport,
  update: updateReport,
  allocate: patchAllocation,
  upload: addNoteDocument,
  remove: removeNoteDocument,
  reload: getReport,
};

export type NoteSave = {
  /** The note being edited; absent when the save creates it. */
  readonly report?: Report | undefined;
  readonly input: ReportInputWave2;
  readonly allocation: Allocation;
  /** False when the allocation is as the note holds it, so no field is sent for it. */
  readonly allocationChanged: boolean;
  readonly uploads: readonly PendingFile[];
  readonly removals: readonly string[];
};

/**
 * One «Salva» (U1 board 1.1): an existing note takes its fields and its projects and client in
 * one PATCH; a new one is created (POST), then given its projects and client (PATCH). The files
 * removed and added follow, one request each. A step that fails after the note is saved is
 * reported in `missed`, never undone.
 */
export async function saveNote(save: NoteSave, api: NoteSaveApi = API): Promise<SaveOutcome> {
  const allocation = save.allocationChanged ? toAllocationPatch(save.allocation) : null;
  const missed: MissedStep[] = [];
  let report: Report;
  if (save.report) {
    const body = allocation ? { ...save.input, ...allocation } : save.input;
    const out = await api.update(save.report.id, save.report.version, body);
    if (!out.ok) return out;
    report = out.value;
  } else {
    const out = await api.create(save.input);
    if (!out.ok) return out;
    report = out.value;
    if (allocation) {
      const allocated = await api.allocate(report.id, report.version, allocation);
      if (allocated.ok) report = allocated.value;
      else missed.push({ kind: 'allocation', code: allocated.code });
    }
  }
  let filesMoved = false;
  for (const documentId of save.removals) {
    const out = await api.remove(report.id, documentId);
    if (out.ok) filesMoved = true;
    else missed.push({ kind: 'remove', documentId, code: out.code });
  }
  for (const file of save.uploads) {
    const out = await api.upload(report.id, file.pdf, file.source, file.label);
    if (out.ok) filesMoved = true;
    else missed.push({ kind: 'upload', key: file.key, name: file.name, code: out.code });
  }
  if (filesMoved) {
    // The note's file count moved; read it back so the page shows it.
    const fresh = await api.reload(report.id);
    if (fresh.ok) report = fresh.value;
  }
  return { ok: true, report, missed };
}
